import { memo, useCallback, useMemo } from 'react';

import { type ScopedPermissions } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { LibraryHeaderUploadButton } from '~/components/LibraryBeta/LibraryHeaderUploadButton';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { CreateDropdownButton } from '~/components/Shared/HeaderBar/CreateDropdownButton';
import { getLibraryCreateMenuOptions } from '~/components/Shared/HeaderBar/getDescriptiveCreateMenuOptions';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useLibraryHeaderUploadMenuOptions } from '~/hooks/useLibraryHeaderUploadActions';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';

export type GeneralLibraryHeaderCreateButtonProps = {
  'data-testid': string;
  workspacePermissions: ScopedPermissions | undefined;
};

export const GeneralLibraryHeaderCreateButton = memo(
  ({ 'data-testid': dataTestId, workspacePermissions }: GeneralLibraryHeaderCreateButtonProps) => {
    const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
    const { openDraftCanvas } = useDraftCanvas();
    const [showCreateBoardModal] = useAirModal(CreateBoardModal);

    const handleCreateBoard = useCallback(() => {
      showCreateBoardModal({
        trackLocation: 'library',
      });
    }, [showCreateBoardModal]);

    const handleCreateCanvas = useCallback(() => {
      openDraftCanvas({
        analyticsContext: { source: 'general_library' },
      });
    }, [openDraftCanvas]);

    const { uploadMenuOptions } = useLibraryHeaderUploadMenuOptions({ permissions: workspacePermissions });

    const createBoard = canCreateBoard(workspacePermissions) ? handleCreateBoard : undefined;
    const createCanvas =
      generativeCanvasEnabled && canCreateCanvasInWorkspace(workspacePermissions) ? handleCreateCanvas : undefined;

    const createMenuOptions = useMemo(
      () =>
        getLibraryCreateMenuOptions({
          onCreateBoard: createBoard,
          onCreateCanvas: createCanvas,
        }),
      [createBoard, createCanvas],
    );

    if (!uploadMenuOptions.length && !createMenuOptions.length) {
      return null;
    }

    return (
      <div className="flex shrink-0 items-center gap-1">
        <LibraryHeaderUploadButton options={uploadMenuOptions} />
        <CreateDropdownButton
          color="gray"
          data-testid={dataTestId}
          label="Create"
          menuClassName="w-80"
          options={createMenuOptions}
        />
      </div>
    );
  },
);

GeneralLibraryHeaderCreateButton.displayName = 'GeneralLibraryHeaderCreateButton';
